import { useId, useState } from 'react';
import { api } from '../api.js';
import { useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { fmtTime } from './format.js';
import { ErrorNotice, Loading, Notice } from './Notice.js';

const RANGES = [
  { minutes: 15, label: 'Last 15 minutes' },
  { minutes: 30, label: 'Last 30 minutes' },
  { minutes: 60, label: 'Last hour' },
  { minutes: 180, label: 'Last 3 hours' },
  { minutes: 720, label: 'Last 12 hours' },
  { minutes: 1440, label: 'Last 24 hours' },
];

export function LogsTab() {
  const { app } = useApp();
  const [minutes, setMinutes] = useState(30);
  const [filterText, setFilterText] = useState('');
  const [filter, setFilter] = useState('');
  const ids = { range: useId(), filter: useId() };
  const logs = useResource(
    () => api.runtimeLogs(app.slug, minutes, filter || undefined),
    [app.slug, minutes, filter],
  );

  return (
    <section className="card">
      <h2>Logs</h2>
      <p className="small subtle">
        What your app printed while running. Handy when a page shows an error and you want to see why.
      </p>
      {!app.currentDeployId ? (
        <Notice kind="info">
          There won't be any logs until a version has been deployed and someone has used it.
        </Notice>
      ) : null}
      <form
        className="btn-row"
        style={{ marginBottom: '1rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (filterText === filter) logs.reload();
          else setFilter(filterText);
        }}
      >
        <label htmlFor={ids.range} className="visually-hidden">
          Time range
        </label>
        <select id={ids.range} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
          {RANGES.map((r) => (
            <option key={r.minutes} value={r.minutes}>
              {r.label}
            </option>
          ))}
        </select>
        <label htmlFor={ids.filter} className="visually-hidden">
          Only lines containing
        </label>
        <input
          id={ids.filter}
          type="search"
          placeholder="Only lines containing…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{ width: 'auto', flex: '1 1 12rem' }}
        />
        <button type="submit" className="btn" disabled={logs.loading}>
          {logs.loading ? 'Loading…' : 'Refresh'}
        </button>
      </form>
      <ErrorNotice error={logs.error} />
      {logs.loading && !logs.data ? <Loading /> : null}
      {logs.data ? (
        <>
          <pre className="log" role="log" aria-live="off" aria-label="Runtime log">
            {logs.data.events.map((l) => `${fmtTime(l.at)}  ${l.message.replace(/\n$/, '')}\n`).join('')}
          </pre>
          <p className="small subtle">
            {logs.data.events.length === 0
              ? 'No log lines in this range.'
              : `${logs.data.events.length} lines${logs.data.next ? ' (showing the first page)' : ''}.`}
          </p>
        </>
      ) : null}
    </section>
  );
}
